import React, { useMemo, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { Gana } from '../api/ganaApi';
import {
  createBartikPadachhedGanaLink,
  deleteBartikPadachhedGanaLink,
  updateBartikPadachhedGanaLink,
  type BartikPadachhedGanaLink,
} from '../api/ashtadhyayiBartikPadachhedGanaApi';

type LinkFormValues = { ganaId: number };

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || 'Request failed';
}

type Props = {
  bartikPadachhedId: number;
  links: BartikPadachhedGanaLink[];
  ganaOptions: Gana[];
  lookupLoading: boolean;
  onChanged: () => Promise<void>;
};

export function BartikPadachhedGanaLinksPanel({ bartikPadachhedId, links, ganaOptions, lookupLoading, onChanged }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BartikPadachhedGanaLink | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<LinkFormValues>();

  const options = useMemo(
    () =>
      ganaOptions.map((g) => ({
        value: g.id,
        label: `${g.name} (#${g.id})`,
        disabled: links.some((l) => l.ganaId === g.id && l.id !== editing?.id),
      })),
    [ganaOptions, links, editing?.id]
  );

  const columns: ColumnsType<BartikPadachhedGanaLink> = [
    { title: 'Gana', render: (_, l) => `${l.gana?.name ?? `#${l.ganaId}`} (#${l.ganaId})` },
    {
      title: 'Actions',
      width: 120,
      render: (_, l) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => { setEditing(l); form.setFieldsValue({ ganaId: l.ganaId }); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete this link?" onConfirm={() => void deleteBartikPadachhedGanaLink(l.id).then(onChanged)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} disabled={lookupLoading}>Add link</Button>
      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={links} locale={{ emptyText: 'No gana links' }} />
      <Modal title={editing ? `Edit link #${editing.id}` : 'Add gana link'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }} onOk={() => void (async () => {
        try {
          const values = await form.validateFields();
          setSaving(true);
          if (editing) {
            if (values.ganaId === editing.ganaId) {
              message.info('No changes to save');
            } else {
              await updateBartikPadachhedGanaLink(editing.id, { ganaId: values.ganaId });
              message.success('Link updated');
            }
          } else {
            await createBartikPadachhedGanaLink({ ashtadhyayiBartikPadachhedId: bartikPadachhedId, ganaId: values.ganaId });
            message.success('Link created');
          }
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
          await onChanged();
        } catch (err: unknown) {
          if ((err as { errorFields?: unknown }).errorFields) return;
          message.error(errMessage(err));
        } finally {
          setSaving(false);
        }
      })()} confirmLoading={saving} destroyOnClose>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="ganaId" label="Gana" rules={[{ required: true, message: 'Select a gana' }]}>
            <Select showSearch optionFilterProp="label" options={options} loading={lookupLoading} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
